/**
 * The DECOR render registry — the inverse of the furniture-builder TAGS. Given a {@link DecorItem}
 * (kind + transform), build the matching Pixi piece from the SAME {@link furniture} catalog the
 * decorators use, so a piece rendered from data is byte-identical to one a decorator placed. This is what
 * lets the editor render furniture as editable DATA (and the office's captured furniture re-render
 * identically). Cosmetic only — decor never touches collision/voice/interaction.
 */
import { Container } from 'pixi.js';
import type { DecorItem, InteractiveObjectKind } from '@duck/contracts';
import type { ThemeArt } from './theme-art';
import * as f from './furniture';
import { buildScenery } from './scenery';
import { buildPropBody } from './props';

/** Render kinds that are INTERACTIVE object props (Furniture Store: coffee/jukebox/tv) — drawn with the
 * SAME object-prop art as the static office objects, so a placed coffee machine looks like the real one. */
const OBJECT_PROP_KINDS = new Set<string>(['coffee', 'jukebox', 'tv']);

/** Build the Pixi piece for one decor item (no transform applied — the caller positions it). */
function buildBody(item: DecorItem, art: ThemeArt): Container {
  switch (item.kind) {
    case 'long-desk':
      return f.longDesk(art, item.w ?? 84);
    case 'desk-chair-up':
      return f.deskChair(art, 'up');
    case 'desk-chair-down':
      return f.deskChair(art, 'down');
    case 'office-chair':
      return f.officeChair(art);
    case 'sofa':
      return f.sofa(art);
    case 'armchair':
      return f.armchair(art);
    case 'coffee-table':
      return f.coffeeTable(art);
    case 'rug':
      return f.rug(art, item.w ?? 160, item.h ?? 90);
    case 'monitor':
      return f.monitor(art);
    case 'computer':
      return f.computer(art);
    case 'bookshelf':
      return f.bookshelf(art);
    case 'cabinet':
      return f.cabinet(art);
    case 'lamp':
      return f.lamp(art);
    case 'whiteboard':
      return f.whiteboard(art);
    case 'plant-leafy':
      return f.plant(art, 'leafy');
    case 'plant-tall':
      return f.plant(art, 'tall');
    case 'plant-fern':
      return f.plant(art, 'fern');
    case 'wall-painting':
      return f.wallArt(art, 'painting');
    case 'wall-poster':
      return f.wallArt(art, 'poster');
    case 'wall-abstract':
      return f.wallArt(art, 'abstract');
    case 'hookah':
      return f.hookah(art);
    case 'game-console':
      return f.gameConsole(art);
    default:
      // Interactive prop (coffee/jukebox/tv) → the object-prop art (matches the static office objects).
      if (OBJECT_PROP_KINDS.has(item.kind)) {
        return buildPropBody(
          { id: 'furn', kind: item.kind as InteractiveObjectKind, icon: '', label: '', x: 0, y: 0, effect: {} },
          art,
        );
      }
      // Not a shared-catalog kind → a room-registered bespoke SCENERY kind (CEO uplighter, arcade, …).
      return buildScenery(item.kind, art) ?? new Container();
  }
}

/** Build a fully-placed decor piece (body + position/rotation/scale) for the map. */
export function buildDecorItem(item: DecorItem, art: ThemeArt): Container {
  const body = buildBody(item, art);
  body.x = item.x;
  body.y = item.y;
  if (item.rotation) body.rotation = item.rotation;
  if (item.scale && item.scale !== 1) body.scale.set(item.scale);
  return body;
}

/**
 * Rugs are floor decals that must sit UNDER other furniture — the map renders them in a separate pass
 * before the room's pieces. Everything else is regular furniture rendered over the floor.
 */
export function isFloorDecor(kind: string): boolean {
  return kind === 'rug';
}

/** One palette entry (dual-coded icon + label) so the editor can offer every furniture/scenery kind. */
export interface DecorCatalogEntry {
  kind: string;
  label: string;
  icon: string;
}

/** The full placeable furniture catalog, grouped loosely (seating · desks · plants · wall · misc). */
export const DECOR_CATALOG: readonly DecorCatalogEntry[] = [
  { kind: 'sofa', label: 'Sofa', icon: '🛋️' },
  { kind: 'armchair', label: 'Armchair', icon: '🪑' },
  { kind: 'office-chair', label: 'Office chair', icon: '💺' },
  { kind: 'desk-chair-up', label: 'Desk chair ↑', icon: '🪑' },
  { kind: 'desk-chair-down', label: 'Desk chair ↓', icon: '🪑' },
  { kind: 'coffee-table', label: 'Coffee table', icon: '🛎️' },
  { kind: 'long-desk', label: 'Desk', icon: '🗄️' },
  { kind: 'monitor', label: 'Monitor', icon: '🖥️' },
  { kind: 'computer', label: 'Computer', icon: '💻' },
  { kind: 'bookshelf', label: 'Bookshelf', icon: '📚' },
  { kind: 'cabinet', label: 'Cabinet', icon: '🗃️' },
  { kind: 'whiteboard', label: 'Whiteboard', icon: '📋' },
  { kind: 'lamp', label: 'Lamp', icon: '💡' },
  { kind: 'rug', label: 'Rug', icon: '🟫' },
  { kind: 'plant-leafy', label: 'Plant', icon: '🪴' },
  { kind: 'plant-tall', label: 'Tall plant', icon: '🌴' },
  { kind: 'plant-fern', label: 'Fern', icon: '🌿' },
  { kind: 'wall-painting', label: 'Painting', icon: '🖼️' },
  { kind: 'wall-poster', label: 'Poster', icon: '📰' },
  { kind: 'wall-abstract', label: 'Abstract art', icon: '🎨' },
  { kind: 'hookah', label: 'Hookah', icon: '💨' },
  { kind: 'game-console', label: 'Game console', icon: '🎮' },
];
